import { getHazardList } from "@/features/branding";
import { getTranslatedText } from "@/features/internationalisation";
import { PortfolioView } from "@/store/services/supabase";
import { PortfolioExportAssetData } from "@/tools/aggregate/portfolio-export/types";
import type { AdaptationType } from "@/types/adaptation";
import { ExposuresByHazard } from "@/types/exposure";

/**
 * Canonical hazard set used across results payloads.
 *
 * Note: branding may choose to *hide* hazards in the UI; use `getHazards()` for the
 * current UI-available hazards list.
 */
export const hazards = [
  "coastal_inundation",
  "cyclone_wind",
  "cyclone_storm_surge",
  "flood_riverine",
  "flood_surfacewater",
  "forest_fire",
  "freeze_thaw",
  "heat",
  "landslide",
  "soil_movement",
  "wind",
] as const;
export const metrics = ["mvar", "tip", "cav", "fp", "pl", "bands"] as const;
export const scenarios = ["rcp26", "rcp45", "rcp60", "rcp85"] as const;
export const years = [
  1990, 1995, 2000, 2005, 2010, 2015, 2020, 2025, 2030, 2035, 2040, 2045, 2050, 2055, 2060, 2065, 2070, 2075, 2080,
  2085, 2090, 2095, 2100,
] as const;
export const returnPeriods = [20, 50, 100, 200, 500, 1000, 1500] as const;

export type Hazard = (typeof hazards)[number];
export type HazardPlusTotal = Hazard | "total";
export type Metric = (typeof metrics)[number];
export type Scenario = (typeof scenarios)[number];
export type Year = (typeof years)[number];

export type ResultsMode = "simple" | "comprehensive";

export const metricNames: Record<
  Metric,
  {
    acronym?: string;
    fullName: string;
    /** Overrides the metric id in filenames, for metrics whose id reads poorly there. */
    filenameAcronym?: string;
  }
> = {
  mvar: {
    acronym: "MVAR",
    fullName: "Maximum-to-Date Value-at-Risk",
  },
  tip: {
    acronym: "TIP",
    fullName: "Technical Insurance Premium",
  },
  cav: {
    acronym: "CAV",
    fullName: "Climate Adjusted Value",
  },
  fp: {
    acronym: "FP",
    fullName: "Failure Probability",
  },
  pl: {
    acronym: "PL",
    fullName: "Productivity Loss",
  },
  bands: {
    fullName: getTranslatedText("terms:simpleRiskRatings"),
    filenameAcronym: getTranslatedText("terms:simpleRiskRatings"),
  },
};
export const scenarioNames: Record<Scenario, string> = {
  rcp26: getTranslatedText("terms:scenarios.rcp26.medium"),
  rcp45: getTranslatedText("terms:scenarios.rcp45.medium"),
  rcp60: getTranslatedText("terms:scenarios.rcp60.medium"),
  rcp85: getTranslatedText("terms:scenarios.rcp85.medium"),
};

const isHazard = (hazard: string): hazard is Hazard => {
  return (hazards as readonly string[]).includes(hazard);
};

/**
 * Returns the hazards currently available in the UI (branding-configured).
 * Falls back to the canonical hazard list when branding config is unavailable (e.g. SSR).
 */
export const getHazards = (): readonly Hazard[] => {
  try {
    return [...new Set(getHazardList().available)].filter(isHazard);
  } catch {
    return hazards;
  }
};

export type ValuesByYear = Record<Year, number | null>;

export type Layer = {
  name: string;
  description: string;
  source: string;
};
export type LayerException = { detail: string; exception: string };

export type Protection = {
  defences: {
    height: number;
    above: string;
    rp: number;
    precipitation: number;
  } | null;
  anomaly_management: {
    height: number;
    above: string;
    rp: number;
    precipitation: number;
  } | null;
};

export type IncorporatedHazard = (typeof hazards)[number];

export type ResultMetadata = {
  geocoding: { source: string; quality: string };
  incorporated_hazards: IncorporatedHazard[];
  location: {
    latitude: number;
    longitude: number;
    elevation?: number;
  };
  elevation: { dataset: string };
  engines_version: string;
  warnings: string[];
  flood_riverine: { protections: Protection; layers: (Layer | LayerException)[] };
  coastal_inundation: { protections: Protection; layers: (Layer | LayerException)[] };
  heat: {
    /** Only reported for the threshold forms the run actually resolved. */
    thresholds?: Partial<{
      return_period: number;
      temperature: number;
    }>;
    protections: Protection;
    layers: (Layer | LayerException)[];
  };
  forest_fire: { protections: Protection; layers: (Layer | LayerException)[] };
  wind: {
    /** Only reported for the threshold forms the run actually resolved. */
    thresholds?: Partial<{
      return_period: number;
      wind_speed: number;
    }>;
    protections: Protection;
    layers: (Layer | LayerException)[];
  };
  soil_movement: { protections: Protection; layers: (Layer | LayerException)[] };
  freeze_thaw: { protections: Protection; layers: (Layer | LayerException)[] };
  flood_surfacewater: { protections: Protection; layers: (Layer | LayerException)[] };
  cyclone_wind: {
    /** Only reported for the threshold forms the run actually resolved. */
    thresholds?: Partial<{
      return_period: number;
      wind_speed: number;
    }>;
    protections: Protection;
    layers: (Layer | LayerException)[];
  };
  cyclone_storm_surge: { protections: Protection; layers: (Layer | LayerException)[] };
  landslide: { protections: Protection; layers: (Layer | LayerException)[] };
};

export type ScenarioMetricResult = {
  total: ValuesByYear;
  by_hazard: Record<Hazard, ValuesByYear>;
};

export type Result = {
  metadata: ResultMetadata;
  results: Record<
    Scenario,
    {
      damage: ScenarioMetricResult;
      failure: ScenarioMetricResult;
      productivity_loss: ScenarioMetricResult;
      exposures: ExposuresByHazard;
    }
  >;
};

/**
 * Minimal response shape used by utilities that only need damage values.
 * Supports both full results (all metrics) and lightweight results (damage-only).
 */
export type DamageOnlyResponse = {
  result: {
    results: Partial<
      Record<
        Scenario,
        {
          damage: ScenarioMetricResult;
        }
      >
    >;
  };
};

/** A successful maximal result. */
export interface ResponseSuccess {
  item_id: string;
  result: Result;
}

/** A successful maximal result with metadata from Supabase. */
export interface ResponseSuccessWithMetadata extends ResponseSuccess {
  metadata: PortfolioExportAssetData;
}

/** Lightweight result per scenario for map visualization - contains only damage/exposures */
export type LightweightResultPerScenario = {
  damage: ScenarioMetricResult;
  exposures: ExposuresByHazard;
  /** Optional in lightweight results; present in full results. */
  failure?: ScenarioMetricResult;
  /** Optional in lightweight results; present in full results. */
  productivity_loss?: ScenarioMetricResult;
};

/** Lightweight result for map visualization - contains one or more scenarios */
export type LightweightResult = {
  metadata: Pick<ResultMetadata, "geocoding" | "incorporated_hazards" | "location" | "engines_version">;
  results: Partial<Record<Scenario, LightweightResultPerScenario>>;
};

/** Lightweight response with metadata for map visualization */
export interface ResponseSuccessLightweight {
  item_id: string;
  result: LightweightResult;
  metadata: PortfolioExportAssetData;
}

/** Transform full result to lightweight for a specific scenario */
export const transformFullToLightweight = (
  full: ResponseSuccessWithMetadata,
  scenario: Scenario
): ResponseSuccessLightweight => {
  return {
    item_id: full.item_id,
    metadata: full.metadata,
    result: {
      metadata: {
        geocoding: full.result.metadata.geocoding,
        incorporated_hazards: full.result.metadata.incorporated_hazards,
        location: full.result.metadata.location,
        engines_version: full.result.metadata.engines_version,
      },
      results: {
        [scenario]: {
          damage: full.result.results[scenario].damage,
          exposures: full.result.results[scenario].exposures,
        },
      },
    },
  };
};

/** A failed maximal run. */
export interface ResponseError {
  item_id: string;
  error: any;
}

export type Response = ResponseSuccess | ResponseError;

export interface LineReducerOutput {
  previousLine: string;
  successes: ResponseSuccess[];
  errors: ResponseError[];
}

/** The distributor used for streaming data from Facades to an output. */
export interface Distributor {
  send(data: ResponseSuccessWithMetadata[] | undefined, adaptationType?: AdaptationType): Promise<any>;
}

export type SummaryWithProgress = PortfolioView & {
  status: string;
  position: number | null;
};

export type AdaptationSummaryWithProgress = {
  id: string;
  summary_id: string | null;
  summary_started_at: string | null;
  name: string;
  success: number | null;
  error: number | null;
  group_name: string | null;
  user_id: string | null;
  status: string;
  position: number | null;
};
